/**
 * Generic Modal Component
 *
 * A reusable portal-based modal with RTL support, backdrop blur,
 * keyboard accessibility (ESC to close), and smooth animations.
 */

import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { useMotion } from '../providers/MotionProvider';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
  icon?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  children: ReactNode;
  showCloseButton?: boolean;
  closeOnBackdrop?: boolean;
  closeOnEscape?: boolean;
  className?: string;
  headerClassName?: string;
  bodyClassName?: string;
}

export default function Modal({
  isOpen,
  onClose,
  title,
  subtitle,
  icon,
  size = 'md',
  children,
  showCloseButton = true,
  closeOnBackdrop = true,
  closeOnEscape = true,
  className = '',
  headerClassName = '',
  bodyClassName = '',
}: ModalProps) {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  // Handle ESC key to close modal
  useEffect(() => {
    if (!isOpen || !closeOnEscape) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, closeOnEscape, onClose]);

  // Prevent body scroll while the modal is open, then restore what was there.
  // A CLOSED modal never touches it: pages mount many closed modals (one per
  // result card), and clearing the style on mount undid the page's own lock
  // (the /search filter drawer) whenever results reloaded under it.
  useEffect(() => {
    if (!isOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isOpen]);

  const { reducedMotion } = useMotion();

  const sizeClasses: Record<Required<ModalProps>['size'], string> = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    full: 'max-w-4xl',
  };

  const handleBackdropClick = () => {
    if (closeOnBackdrop) {
      onClose();
    }
  };

  // Animation variants
  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
  };

  const modalVariants = {
    hidden: {
      opacity: 0,
      scale: 0.95,
      y: 20,
    },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
    },
  };

  const transition = reducedMotion
    ? { duration: 0 }
    : { duration: 0.25, ease: [0.22, 1, 0.36, 1] };

  const modalContent = (
    <AnimatePresence mode="wait">
      {isOpen && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={handleBackdropClick}
            aria-hidden="true"
            initial="hidden"
            animate="visible"
            exit="hidden"
            variants={backdropVariants}
            transition={transition}
          />

          {/* Modal */}
          <motion.div
            className={`relative w-full ${sizeClasses[size]} rounded-3xl bg-white dark:bg-[var(--pv-color-surface-strong)] shadow-2xl ${className}`}
            dir={isRTL ? 'rtl' : 'ltr'}
            role="dialog"
            aria-modal="true"
            aria-labelledby={title ? 'modal-title' : undefined}
            onClick={(e) => e.stopPropagation()}
            initial="hidden"
            animate="visible"
            exit="hidden"
            variants={modalVariants}
            transition={transition}
          >
            {/* Header */}
            {(title || showCloseButton) && (
              <div className={`flex items-center justify-between border-b border-[var(--pv-border-soft)] p-4 ${headerClassName}`}>
                <div className="flex items-center gap-3">
                  {icon && (
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[var(--pv-color-primary)] text-white">
                      <i className={`${icon} text-xl`}></i>
                    </div>
                  )}
                  {title && (
                    <div>
                      <h3
                        id="modal-title"
                        className="font-semibold text-[var(--pv-color-text)]"
                      >
                        {title}
                      </h3>
                      {subtitle && (
                        <p className="text-xs text-[var(--pv-color-text-muted)] truncate max-w-[250px]">
                          {subtitle}
                        </p>
                      )}
                    </div>
                  )}
                </div>
                {showCloseButton && (
                  <motion.button
                    onClick={onClose}
                    className="rounded-lg p-2 text-[var(--pv-color-text-muted)] hover:bg-gray-100 dark:hover:bg-[var(--pv-color-surface-soft)] transition"
                    aria-label="Close modal"
                    whileHover={reducedMotion ? {} : { scale: 1.1 }}
                    whileTap={reducedMotion ? {} : { scale: 0.95 }}
                  >
                    <i className="ri-close-line text-xl"></i>
                  </motion.button>
                )}
              </div>
            )}

            {/* Content */}
            <div className={`p-4 ${bodyClassName}`}>
              {children}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );

  return createPortal(modalContent, document.body);
}
